import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  AddAddressErrorMessages,
  AddressFields,
  ErrorOption
} from '@com-delta-idp/mnx-login-ui';
import { ADD_ADDRESS_ERROR_ID } from '../../../validator/add-address-validator';
import {
  AddressLine,
  AddressLinesResponse,
  CountryDetails,
  DropdownField
} from '../../models/add-address.interface';
import { CustomValidatorService } from './custom-validator.service';

@Injectable({
  providedIn: 'root'
})
export class AddAddressHelperSupportService extends CustomValidatorService {
  stateLabel = '';
  stateList: DropdownField[] = [];

  constructor() {
    super();
  }

  transformCountryCodes(
    countryDetails: CountryDetails[],
    defaultCountry: string
  ): DropdownField[] {
    const countryList = [];
    for (const country in countryDetails) {
      const { countryCd, countryNm } = countryDetails[country];
      if (defaultCountry !== countryCd) {
        countryList.push({
          value: countryCd,
          label: countryNm
        });
      } else {
        countryList.unshift({
          value: countryCd,
          label: countryNm
        });
      }
    }
    return countryList;
  }

  transformStatesList(
    stateList: { code: string; value: string }[] = []
  ): DropdownField[] {
    if (stateList.length === 0) {
      return [];
    }
    return stateList.map(({ code: value, value: label }) => ({
      value,
      label
    }));
  }

  transformAddressLines(
    addressLines: AddressLinesResponse,
    formGroup?: UntypedFormGroup
  ): AddressFields {
    const addressFields = addressLines.addressFields;
    const transformedFields = {} as AddressFields;
    for (const [
      key,
      { visible, label = '', required, regex = '' }
    ] of Object.entries(addressFields)) {
      const addressLine = key as keyof typeof addressFields;
      transformedFields[addressLine] = {
        visible: !!(visible === 'true'),
        label: `${label + (required === 'true' ? '*' : '')}`,
        required: !!(required === 'true'),
        regex
      };
    }
    this.setStateDetails(addressLines);
    if (formGroup) {
      this.setValidator(transformedFields, formGroup);
    }
    return transformedFields;
  }

  initialErrorMessage(
    option: ErrorOption,
    errorMessages: AddAddressErrorMessages
  ) {
    if (option['errorId'] === ADD_ADDRESS_ERROR_ID.INVALID_ADDR_LINE_1) {
      errorMessages.addressLine1ErrorMessage.INVALID_ADDR_LINE_1 =
        option['errorMessageDescription'];
    } else if (option['errorId'] === ADD_ADDRESS_ERROR_ID.INVALID_ADDR_LINE_2) {
      errorMessages.addressLine2ErrorMessage.INVALID_ADDR_LINE_2 =
        option['errorMessageDescription'];
    } else if (option['errorId'] === ADD_ADDRESS_ERROR_ID.INVALID_DISTRICT) {
      errorMessages.districtErrorMessage.INVALID_DISTRICT =
        option['errorMessageDescription'];
    }
    this.initialErrorMessageHelper(option, errorMessages);
  }

  initialErrorMessageHelper(
    option: ErrorOption,
    errorMessages: AddAddressErrorMessages
  ) {
    if (option['errorId'] === ADD_ADDRESS_ERROR_ID.INVALID_CITY) {
      errorMessages.cityErrorMessage.INVALID_CITY =
        option['errorMessageDescription'];
    } else if (option['errorId'] === ADD_ADDRESS_ERROR_ID.SELECT_STATE) {
      errorMessages.stateErrorMessage.SELECT_STATE =
        option['errorMessageDescription'];
    } else if (option['errorId'] === ADD_ADDRESS_ERROR_ID.INVALID_ZIP_CODE) {
      errorMessages.postalCodeErrorMessage.INVALID_ZIP_CODE =
        option['errorMessageDescription'];
    }
    this.initialErrorMessageHelperSupport(option, errorMessages);
  }

  initialErrorMessageHelperSupport(
    option: ErrorOption,
    errorMessages: AddAddressErrorMessages
  ): void {
    if (option['errorId'] === ADD_ADDRESS_ERROR_ID.SELECT_PREFECTURE) {
      errorMessages.stateErrorMessage.SELECT_PREFECTURE =
        option['errorMessageDescription'];
    }
  }

  setStateDetails(addressLines: AddressLinesResponse): void {
    if (addressLines.addressFields.addressLine5.visible === 'true') {
      this.stateListAndLabel(addressLines.addressFields.addressLine5);
    }
    if (addressLines.addressFields.addressLine7.visible === 'true') {
      this.stateListAndLabel(addressLines.addressFields.addressLine7);
    }
  }

  stateListAndLabel(addressLine: AddressLine): void {
    const { values, label = '', required } = addressLine;
    this.stateList = this.transformStatesList(values);
    this.stateLabel = `${label + (required === 'true' ? '*' : '')}`;
  }
}
